// Dates and clock times for both languages (round2-i18n.md §6 "Dates", "Clock",
// "Relative time"). Day and month names are dictionary keys (format.day.*,
// format.day_long.*, format.month.*, format.month_long.*); every phrase shape is a
// template key, so bn keeps its own word order.
//
// Time zone: every function reads the Date's LOCAL fields (getDate/getHours…),
// as the designs do. Callers hand in a Date already expressed in the display
// zone (in a Worker, local = UTC; shift before calling if needed).
//
//   formatDate('en', d)               → "7 Jul 2026"            bn "৭ জুলাই ২০২৬"
//   formatDateLong('en', d)           → "Tue 7 Jul, 2026"       bn "মঙ্গল ৭ জুলাই, ২০২৬"
//   formatMonthYear('en', d)          → "Mar 2026"              bn "মার্চ ২০২৬"
//   formatDateRange('en', a, b)       → "20–22 Mar"             bn "২০–২২ মার্চ"
//   formatClock('en', 16, 30)         → "4:30 PM"               bn "বিকাল ৪:৩০"
//   formatClock('en', 10, 0)          → "10 AM"                 bn "সকাল ১০টা"
//   formatDayPhrase('en', d, now)     → "Today, 4:30 PM"        bn "আজ, বিকাল ৪:৩০"
//   formatRelativeDay('en', d, now)   → "Tomorrow" / "In 3 days" / "2 days ago"
//   formatTimeAgo('en', d, now)       → "Just now" / "5 min ago" / "Last week"
import type { Lang } from './translate';
import { t } from './translate';
import { localiseDigits } from './format-number';
import { formatMessage } from './interpolate';

const DAY_IDS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
const MONTH_IDS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const;

export type NameWidth = 'short' | 'long';

// ---------------------------------------------------------------- names

// 0 = Sunday … 6 = Saturday (Date#getDay). en "Tue"/"Tuesday", bn "মঙ্গল"/"মঙ্গলবার".
export function dayName(lang: Lang, weekday: number, width: NameWidth = 'short'): string {
  const id = DAY_IDS[((weekday % 7) + 7) % 7] ?? 'sun';
  return t(lang, width === 'long' ? `format.day_long.${id}` : `format.day.${id}`);
}

// 0 = January … 11 = December (Date#getMonth). en "Jul"/"July", bn "জুলাই".
// Short bn names follow the website/admin tables ("জানু", "সেপ্ট").
export function monthName(lang: Lang, month: number, width: NameWidth = 'short'): string {
  const id = MONTH_IDS[((month % 12) + 12) % 12] ?? 'jan';
  return t(lang, width === 'long' ? `format.month_long.${id}` : `format.month.${id}`);
}

// ---------------------------------------------------------------- dates

export interface DateOptions {
  year?: boolean; // default true
  month?: NameWidth; // default 'short'
}

// Years are never grouped ("2026", not "2,026").
const year = (lang: Lang, d: Date) => localiseDigits(lang, d.getFullYear());
const dom = (lang: Lang, d: Date) => localiseDigits(lang, d.getDate());

// "7 Jul 2026" / "৭ জুলাই ২০২৬"; with { year: false } → "7 Jul".
export function formatDate(lang: Lang, d: Date, opts: DateOptions = {}): string {
  const month = monthName(lang, d.getMonth(), opts.month ?? 'short');
  if (opts.year === false)
    return formatMessage(lang, 'format.date.day_month', { d: dom(lang, d), month });
  return formatMessage(lang, 'format.date.short', { d: dom(lang, d), month, year: year(lang, d) });
}

// "Tue 7 Jul, 2026" / "মঙ্গল ৭ জুলাই, ২০২৬" (website fmtDateLong).
export function formatDateLong(
  lang: Lang,
  d: Date,
  opts: { day?: NameWidth; month?: NameWidth } = {},
): string {
  return formatMessage(lang, 'format.date.long', {
    day: dayName(lang, d.getDay(), opts.day ?? 'short'),
    d: dom(lang, d),
    month: monthName(lang, d.getMonth(), opts.month ?? 'short'),
    year: year(lang, d),
  });
}

// "Mar 2026" / "মার্চ ২০২৬" (customer monthLabel).
export function formatMonthYear(lang: Lang, d: Date, width: NameWidth = 'short'): string {
  return formatMessage(lang, 'format.date.month_year', {
    month: monthName(lang, d.getMonth(), width),
    year: year(lang, d),
  });
}

// "20–22 Mar" within a month; "30 Mar – 2 Apr" across months; full dates across
// years. (The customer design's `range()` always used the end month; this
// extends it to cross-month ranges.) Same day → "20 Mar".
export function formatDateRange(lang: Lang, a: Date, b: Date, width: NameWidth = 'short'): string {
  const sameYear = a.getFullYear() === b.getFullYear();
  const sameMonth = sameYear && a.getMonth() === b.getMonth();
  if (sameMonth && a.getDate() === b.getDate())
    return formatDate(lang, a, { year: false, month: width });
  if (sameMonth) {
    return formatMessage(lang, 'format.date.range_same_month', {
      d1: dom(lang, a),
      d2: dom(lang, b),
      month: monthName(lang, b.getMonth(), width),
    });
  }
  const opts: DateOptions = { year: !sameYear, month: width };
  return formatMessage(lang, 'format.date.range', {
    from: formatDate(lang, a, opts),
    to: formatDate(lang, b, opts),
  });
}

// ---------------------------------------------------------------- clock

// Day-part buckets. en renders AM/PM; bn renders the day-part word BEFORE the
// time (customer `clock()`, merchant `fmtTime()`). Boundaries reconcile the
// customer clock() (রাত <5, সকাল <12, দুপুর <16, বিকাল <19, রাত after) with the
// hand-authored design copy, which says "বিকাল ৫টা", "সন্ধ্যা ৬টা", "সন্ধ্যা ৭টা",
// "রাত ৯টা" — so সন্ধ্যা covers 18:00–19:59 — and add ভোর for 04:00–05:59.
export type DayPeriod =
  'late_night' | 'dawn' | 'morning' | 'midday' | 'afternoon' | 'evening' | 'night';

export function dayPeriod(hour: number): DayPeriod {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  if (h < 4) return 'late_night';
  if (h < 6) return 'dawn';
  if (h < 12) return 'morning';
  if (h < 16) return 'midday';
  if (h < 18) return 'afternoon';
  if (h < 20) return 'evening';
  return 'night';
}

export interface ClockOptions {
  // Merchant design always shows minutes ("4:00 PM"); customer drops ":00" and
  // bn reads whole hours as "৪টা". Default false (customer rule).
  alwaysMinutes?: boolean;
}

// 12-hour clock with the period word. hour 0–23, minute 0–59.
export function formatClock(
  lang: Lang,
  hour: number,
  minute: number,
  opts: ClockOptions = {},
): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const time =
    minute === 0 && !opts.alwaysMinutes
      ? formatMessage(lang, 'format.clock.hour', { h: localiseDigits(lang, h12) })
      : formatMessage(lang, 'format.clock.hour_minute', {
          h: localiseDigits(lang, h12),
          m: localiseDigits(lang, String(minute).padStart(2, '0')),
        });
  return formatMessage(lang, 'format.clock.pattern', {
    time,
    period: t(lang, `format.clock.period.${dayPeriod(hour)}`),
  });
}

// Clock for a Date's local time.
export function formatTime(lang: Lang, d: Date, opts: ClockOptions = {}): string {
  return formatClock(lang, d.getHours(), d.getMinutes(), opts);
}

// 24-hour "09:30" / "০৯:৩০" (admin HH:MM patterns).
export function formatClock24(lang: Lang, hour: number, minute: number): string {
  const s = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return localiseDigits(lang, s);
}

// ---------------------------------------------------------------- relative

// Calendar-day distance from `now` to `d` on local dates (0 today, 1 tomorrow,
// −1 yesterday). Counted on date parts, so DST and evening times don't skew it.
export function calendarDayDiff(d: Date, now: Date): number {
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const b = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((b - a) / 86_400_000);
}

// "Today" / "Tomorrow" / "Yesterday" / "In 3 days" / "3 days ago".
export function formatRelativeDay(lang: Lang, d: Date, now: Date): string {
  const diff = calendarDayDiff(d, now);
  if (diff === 0) return t(lang, 'format.relative.today');
  if (diff === 1) return t(lang, 'format.relative.tomorrow');
  if (diff === -1) return t(lang, 'format.relative.yesterday');
  if (diff > 0) return formatMessage(lang, 'format.relative.in_days', { n: diff });
  return formatMessage(lang, 'format.relative.days_ago', { n: -diff });
}

// Customer `phrase()`: Today/Tomorrow/Yesterday, else "20 Mar"; then ", <clock>"
// unless { timeless: true }. "আজ, বিকাল ৪:৩০" / "আগামীকাল, সকাল ১০টা".
export function formatDayPhrase(
  lang: Lang,
  d: Date,
  now: Date,
  opts: { timeless?: boolean; month?: NameWidth } & ClockOptions = {},
): string {
  const diff = calendarDayDiff(d, now);
  const head =
    diff === 0
      ? t(lang, 'format.relative.today')
      : diff === 1
        ? t(lang, 'format.relative.tomorrow')
        : diff === -1
          ? t(lang, 'format.relative.yesterday')
          : formatDate(lang, d, { year: false, month: opts.month ?? 'short' });
  if (opts.timeless) return head;
  return formatMessage(lang, 'format.date.with_time', {
    date: head,
    time: formatTime(lang, d, opts),
  });
}

// Past timestamps (activity feeds, reviews, admin logs):
//   < 1 min "Just now" · < 1 h "N min ago" · < 24 h "N h ago" · then calendar
//   days: "Yesterday", "N days ago" (< 7), "Last week" (7–13), "N weeks ago"
//   (< 5 weeks), else the date ("7 Jul 2026"). Future timestamps fall back to
//   formatRelativeDay.
export function formatTimeAgo(lang: Lang, d: Date, now: Date): string {
  const ms = now.getTime() - d.getTime();
  if (ms < 0) return formatRelativeDay(lang, d, now);
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return t(lang, 'format.relative.just_now');
  if (minutes < 60) return formatMessage(lang, 'format.relative.minutes_ago', { n: minutes });
  if (minutes < 24 * 60)
    return formatMessage(lang, 'format.relative.hours_ago', { n: Math.floor(minutes / 60) });
  const days = -calendarDayDiff(d, now);
  if (days <= 1) return t(lang, 'format.relative.yesterday');
  if (days < 7) return formatMessage(lang, 'format.relative.days_ago', { n: days });
  if (days < 14) return t(lang, 'format.relative.last_week');
  if (days < 35)
    return formatMessage(lang, 'format.relative.weeks_ago', { n: Math.floor(days / 7) });
  return formatDate(lang, d);
}
